// Input handling for the Field Cameras viewer, kept free of React and the DOM's layout so it can
// be unit-tested: wheel/pinch zoom factors, the single-lens zoom and pan, and the viewer's
// keyboard shortcuts.

export const FRAME_FADE_MS = 200;
export const SLOW_FRAME_MS = 300;
export const PANORAMA_FOV = { min: 30, max: 120, initial: 90 };
export const LENS_MAX_SCALE = 8;

export const prefersReducedMotion = () => {
  try {
    return !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  } catch {
    return false;
  }
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Zoom factor for one wheel event (> 1 zooms in). A mouse-wheel notch (about 100 px) zooms by
 * about 14%. Trackpad pinch arrives as wheel events with ctrlKey set and small deltas, so it is
 * scaled up to follow the fingers. Line and page delta modes (Firefox, some mice) are
 * converted to pixels first.
 */
export function wheelZoomFactor({ deltaY = 0, deltaMode = 0, ctrlKey = false } = {}) {
  const pixels = deltaY * (deltaMode === 1 ? 16 : deltaMode === 2 ? 400 : 1);
  const factor = Math.exp(-pixels * (ctrlKey ? 0.01 : 0.0015));
  return clamp(factor, 0.5, 2);
}

/** The panorama's field of view after zooming by `factor`. */
export const zoomFov = (fov, factor) =>
  clamp(fov / factor, PANORAMA_FOV.min, PANORAMA_FOV.max);

/**
 * Single-lens view: `scale` and the top-left offset (`x`, `y`) as fractions of the viewer box,
 * so the view survives a resize (entering or leaving fullscreen) unchanged. The offset is
 * clamped so the zoomed image always covers the box.
 */
export const LENS_START = { scale: 1, x: 0, y: 0 };

export function clampPan(view) {
  const scale = clamp(view.scale, 1, LENS_MAX_SCALE);
  return {
    scale,
    x: clamp(view.x, 1 - scale, 0),
    y: clamp(view.y, 1 - scale, 0),
  };
}

/** Zoom by `factor` about `point` ({x, y} as fractions of the box), keeping that point still. */
export function zoomView(view, factor, point = { x: 0.5, y: 0.5 }) {
  const scale = clamp(view.scale * factor, 1, LENS_MAX_SCALE);
  const ratio = scale / view.scale;
  return clampPan({
    scale,
    x: point.x - (point.x - view.x) * ratio,
    y: point.y - (point.y - view.y) * ratio,
  });
}

/** Move the view by a fraction of the box. */
export const panView = (view, dx, dy) =>
  clampPan({ ...view, x: view.x + dx, y: view.y + dy });

const TEXT_INPUTS = new Set([
  "text", "search", "email", "url", "tel", "password", "number",
  "date", "datetime-local", "month", "time", "week",
]);

const isElement = (element) => !!element && element.nodeType === 1;

/** An element the user types into: shortcuts never act while it has focus. */
export function isTypingTarget(element) {
  if (!isElement(element)) return false;
  if (
    element.isContentEditable ||
    element.closest?.('[contenteditable]:not([contenteditable="false"])')
  )
    return true;
  const tag = element.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  return tag === "INPUT" && TEXT_INPUTS.has(element.type);
}

const SPACE_INPUTS = new Set([
  "button", "submit", "reset", "checkbox", "radio", "file", "color", "image",
]);

const activatesWithSpace = (element) =>
  isElement(element) &&
  (element.tagName === "BUTTON" ||
    element.tagName === "SUMMARY" ||
    element.getAttribute("role") === "button" ||
    (element.tagName === "INPUT" && SPACE_INPUTS.has(element.type)));

const stepsWithArrows = (element) =>
  isElement(element) &&
  element.tagName === "INPUT" &&
  (element.type === "range" || element.type === "radio");

/**
 * The viewer shortcut for a keydown, or null: Space plays/pauses, Left/Right step one capture,
 * [ and ] mark the range start and end. Never while typing, never with Ctrl/Alt/Meta, and never
 * where the browser already gives the key a meaning (Space on a button, arrows on a slider).
 * Shift+Left/Right are left to the views, which use them to look around.
 */
export function viewerKeyAction(event) {
  if (!event || event.ctrlKey || event.metaKey || event.altKey) return null;
  const target = event.target;
  if (isTypingTarget(target)) return null;
  switch (event.key) {
    case " ":
    case "Spacebar":
      return activatesWithSpace(target) ? null : "togglePlay";
    case "ArrowLeft":
      return event.shiftKey || stepsWithArrows(target) ? null : "previous";
    case "ArrowRight":
      return event.shiftKey || stepsWithArrows(target) ? null : "next";
    case "[":
      return "markStart";
    case "]":
      return "markEnd";
    default:
      return null;
  }
}
